import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useI18n } from '../../../../contexts/I18nContext';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import './MapInfoCard.scss';

const defaultCenter = { lat: 64.1466, lng: -21.9426 };
const mapContainerStyle = { width: '100%', height: '100%' };
const GMAP_LIBRARIES = ['places'];

const MapInfoCard = ({ id, title, description, required = false, value, onChange, disabled = false, props = {} }) => {
  const { t, language } = useI18n();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyAzdX08wbxxvxwih0P73Ij6h-tUVZi0hpo';
  const [position, setPosition] = useState(value?.coords || defaultCenter);
  const [address, setAddress] = useState(value?.address || '');
  const [weather, setWeather] = useState(value?.weather || null);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [resolvingAddress, setResolvingAddress] = useState(false);
  const [open, setOpen] = useState(false);
  const initGeoRef = useRef(false);
  const lastEmittedRef = useRef(null);
  const autocompleteContainerRef = useRef(null);

  const locale = language && language.startsWith('zh') ? 'zh-CN' : 'en';
  const { isLoaded } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: apiKey, libraries: GMAP_LIBRARIES, MapIds: undefined });

  const fetchWeather = useCallback(async (coords) => {
    try {
      setLoadingWeather(true);
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lng}&current_weather=true`;
      const res = await fetch(url);
      const data = await res.json();
      setWeather(data?.current_weather || null);
    } finally {
      setLoadingWeather(false);
    }
  }, []);

  const resolveAddress = useCallback((coords) => {
    const run = async () => {
      try {
        const googleUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coords.lat},${coords.lng}&language=${locale}&key=${apiKey}`;
        const googleRes = await fetch(googleUrl);
        const googleData = await googleRes.json();
        const result = (googleData.results && googleData.results[0]) || null;

        if (result) {
          const comps = result.address_components || [];
          const hasHan = (s) => /[\u4e00-\u9fff]/.test(s || '');
          const pick = (type) => {
            const c = comps.find((x) => x.types && x.types.includes(type));
            return c ? c.long_name : '';
          };
          const route = pick('route');
          const num = pick('street_number');
          const sublocal1 = pick('sublocality_level_1') || pick('sublocality');
          const neighborhood = pick('neighborhood');
          const locality = pick('locality');
          const admin2 = pick('administrative_area_level_2');
          const admin1 = pick('administrative_area_level_1');
          const postcode = pick('postal_code');
          const country = pick('country');
          const line1 = [route, num].filter(Boolean).join(' ');
          const line2 = [sublocal1 || neighborhood].filter(Boolean).join('');
          const line3 = [locality, admin2].filter(Boolean).join(', ');
          const line4 = [admin1, postcode].filter(Boolean).join(' ');
          const last = country;

          if (locale.startsWith('zh')) {
            const parts = [line1, line2, line3, line4, last]
              .filter(Boolean)
              .filter((p) => hasHan(p));
            const formatted = parts.join('，');
            setAddress(formatted || result.formatted_address || '');
          } else {
            const formatted = [line1, line2, line3, line4, last].filter(Boolean).join(', ');
            setAddress(formatted || result.formatted_address || '');
          }
          setResolvingAddress(false);
          return;
        }
      } catch {}

      try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}&accept-language=${locale}`;
        const res = await fetch(url);
        const data = await res.json();
        const addr = data?.address || null;
        if (addr) {
          const city = addr.city || addr.town || addr.village || addr.hamlet || '';
          const line1 = [addr.house_number, addr.road].filter(Boolean).join(' ');
          const line2 = [addr.suburb, addr.neighbourhood].filter(Boolean).join(', ');
          const line3 = [city, addr.state, addr.postcode].filter(Boolean).join(', ');
          const country = addr.country || '';
          const parts = [line1, line2, line3, country].filter(Boolean);
          setAddress(parts.join(', '));
        } else {
          setAddress(data?.display_name || '');
        }
      } catch {
        setAddress('');
      } finally {
        setResolvingAddress(false);
      }
    };

    setResolvingAddress(true);
    run();
  }, [locale, apiKey]);

  useEffect(() => {
    if (!value) return;
    if (value.coords) setPosition(value.coords);
    if (value.address) setAddress(value.address);
    if (value.weather) setWeather(value.weather);
  }, [value]);

  useEffect(() => {
    if (initGeoRef.current) return;
    initGeoRef.current = true;
    if (value?.coords) return;
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setPosition(coords);
          resolveAddress(coords);
          fetchWeather(coords);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 300000 }
      );
    }
  }, [value, resolveAddress, fetchWeather, onChange, address, weather]);

  useEffect(() => {
    if (!onChange || !position || open) return;
    const next = { coords: position, address, weather };
    const prev = lastEmittedRef.current;
    const sameCoords =
      prev &&
      prev.coords &&
      prev.coords.lat === next.coords.lat &&
      prev.coords.lng === next.coords.lng;
    const sameAddress = prev && prev.address === next.address;
    const sameWeather =
      prev &&
      JSON.stringify(prev.weather || null) === JSON.stringify(next.weather || null);
    if (sameCoords && sameAddress && sameWeather) {
      return;
    }
    lastEmittedRef.current = next;
    onChange(next);
  }, [position, address, weather, onChange, open]);

  useEffect(() => {
    const container = autocompleteContainerRef.current;
    if (!open || !isLoaded || !container) return;
    let element;
    (async () => {
      await window.google.maps.importLibrary('places');
      element = new window.google.maps.places.PlaceAutocompleteElement({});
      container.innerHTML = '';
      container.appendChild(element);
      element.addEventListener('gmp-select', async ({ placePrediction }) => {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
        const coords = { lat: place.location.lat(), lng: place.location.lng() };
        setPosition(coords);
        setAddress(place.formattedAddress || place.displayName || '');
        fetchWeather(coords);
      });
    })();
    return () => {
      if (element && container) {
        try {
          container.removeChild(element);
        } catch {}
      }
    };
  }, [open, isLoaded, fetchWeather]);

  const onMapClick = (e) => {
    const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setPosition(coords);
    resolveAddress(coords);
    fetchWeather(coords);
  };

  const confirmSelection = () => {
    setOpen(false);
  };

  const weatherText = useMemo(() => {
    if (!weather) return '';
    const map = {
      0: 'clear sky', 1: 'mainly clear', 2: 'partly cloudy', 3: 'overcast',
      45: 'fog', 48: 'depositing rime fog', 51: 'light drizzle', 53: 'moderate drizzle', 55: 'dense drizzle',
      56: 'freezing light drizzle', 57: 'freezing dense drizzle', 61: 'slight rain', 63: 'moderate rain', 65: 'heavy rain',
      66: 'freezing rain', 67: 'heavy freezing rain', 71: 'slight snow fall', 73: 'moderate snow fall', 75: 'heavy snow fall',
      77: 'snow grains', 80: 'rain showers', 81: 'moderate rain showers', 82: 'violent rain showers', 85: 'snow showers', 86: 'heavy snow showers',
      95: 'thunderstorm', 96: 'thunderstorm with slight hail', 99: 'thunderstorm with heavy hail'
    };
    const code = weather.weathercode;
    const label = map[code] || 'unknown';
    const isNight = weather.is_day === 0;
    return `${label}${isNight ? ' at night' : ''}, ${weather.temperature}°C`;
  }, [weather]);

  return (
    <div className="map-info-card">
      <div className="component-header">
        <h3 className="component-title">
          {title}
          {required && <span className="required-mark">*</span>}
        </h3>
        {description && <p className="component-description">{description}</p>}
      </div>

      <div className="info-card enhanced">
        <div className="info-grid">
          <div className="info-item">
            <div className="label">{t('aiWorkbench.tools.worldView.mapCard.location')}</div>
            <div className="value">{address || t('aiWorkbench.tools.worldView.mapCard.notSelected')}</div>
          </div>
          <div className="info-item">
            <div className="label">{t('aiWorkbench.tools.worldView.mapCard.coords')}</div>
            <div className="value">{position ? `${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}` : '-'}</div>
          </div>
          <div className="info-item">
            <div className="label">{t('aiWorkbench.tools.worldView.mapCard.weather')}</div>
            <div className="value">{loadingWeather ? t('aiWorkbench.tools.worldView.mapCard.loading') : (weatherText || '-')}</div>
          </div>
        </div>
        <div className="card-actions">
          <button className="open-map-btn" type="button" disabled={!isLoaded || disabled} onClick={() => { setOpen(true); if (position && !address) resolveAddress(position); }}>
            {t('aiWorkbench.tools.worldView.mapCard.openMapButton')}
          </button>
        </div>
      </div>

      {open && (
        <div className="map-modal" onClick={() => setOpen(false)}>
          <div className="map-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="map-header">
              <div ref={autocompleteContainerRef} className="search-input"></div>
              <div className="selected-info">
                <span className="selected-label">{t('aiWorkbench.tools.worldView.mapCard.selectedPoint')}</span>
                <span className="selected-value">{address || (resolvingAddress ? t('aiWorkbench.tools.worldView.mapCard.resolving') : t('aiWorkbench.tools.worldView.mapCard.notSelected'))}</span>
              </div>
              <button className="close-btn" onClick={() => setOpen(false)}>×</button>
            </div>
            <div className="map-body">
              {isLoaded && (
                <GoogleMap mapContainerStyle={mapContainerStyle} center={position || defaultCenter} zoom={12} onClick={onMapClick}>
                  {position && <Marker position={position} />}
                </GoogleMap>
              )}
            </div>
            <div className="map-footer">
              <button className="confirm-btn" onClick={confirmSelection}>{t('aiWorkbench.tools.worldView.mapCard.confirmButton')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapInfoCard;
